import { sendNotification } from 'components'
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { TableRequestV3, TableResponse } from 'services/types'
import { isDummyClient } from 'utils'
import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  CheckUsernameRequest,
  CheckUsernameResponse,
  CreateUserRequest,
  CreateUserResponse,
  DeleteUserRequest,
  DeleteUserResponse,
  GetUserPermissionsResponse,
  PermissionType,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  UserDetailsRequest,
  UserDetailsResponse,
} from './types'

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: ['Users', 'User Detail'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL}/user`,
  }),
  endpoints: (builder) => ({
    getUsers: builder.query<TableResponse<{}>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `/clients/${clientId}/list/`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: TableResponse<{}>, meta, arg) => {
        if (!isDummyClient(Number(arg.clientId))) {
          return response
        }

        // Additional user groups for dummy client
        const userGroups = [
          'Afvalinzameling Supervisor',
          'Admin',
          'Recycling Beheer',
          'Milieu Coördinator',
        ]

        // Add new column
        const updatedColumns = [
          ...response.columns.slice(0, 4),
          {
            cellType: 'DEFAULT' as const,
            header: 'Gebruikersgroepen',
            accessor: 'gebruikersgroepen',
            id: 'gebruikersgroepen',
            filterType: 'TEXT' as const,
            disableFilters: false,
            disableSortBy: false,
            isHidden: false,
            hidable: true,
            isVisible: true,
            filterOptions: [],
          },
          ...response.columns.slice(4),
        ]

        // Add a random user group to each row
        const updatedData = response.data.map((row) => ({
          ...row,
          gebruikersgroepen: userGroups[Math.floor(Math.random() * userGroups.length)],
        }))

        return {
          ...response,
          columns: updatedColumns,
          data: updatedData,
        }
      },
      providesTags: ['Users', 'User Detail'],
    }),
    deleteUser: builder.mutation<DeleteUserResponse, DeleteUserRequest>({
      query: ({ clientId, ...data }) => ({
        url: `/clients/${clientId}/delete/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: ({ clientId, ...data }) => ({
        url: `/clients/${clientId}/create/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    updateUser: builder.mutation<UpdateUserResponse, UpdateUserRequest>({
      query: ({ clientId, ...data }) => ({
        url: `/clients/${clientId}/update/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users', 'User Detail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getUserDetails: builder.query<UserDetailsResponse, UserDetailsRequest>({
      query: ({ clientId, userId }) => ({
        url: `/clients/${clientId}/detail/${userId}/`,
        method: 'GET',
      }),
    }),
    getUserPermissions: builder.query<GetUserPermissionsResponse, void>({
      query: () => ({
        url: `/permissions/`,
        method: 'GET',
      }),
    }),
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: ({ ...data }) => ({
        url: `/change_password/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users', 'User Detail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfileRequest>({
      query: ({ ...data }) => ({
        url: `/update_profile/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users', 'User Detail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    checkUsername: builder.mutation<CheckUsernameResponse, CheckUsernameRequest>({
      query: ({ ...data }) => ({
        url: '/check_username/',
        method: 'POST',
        data,
      }),
    }),
    getUserDropdownList: builder.query<
      Array<{
        client_user_id: number
        django_user_id: number
        username: string
        email: string | null
        name: string | null
        surname: string | null
      }>,
      { clientId: number; operations_ids?: number[]; permissions?: PermissionType[] }
    >({
      query: ({ clientId, operations_ids, permissions }) => ({
        url: `/clients/${clientId}/dropdown_list/`,
        method: 'GET',
        params: {
          operations_ids: operations_ids?.join(','),
          permissions: permissions?.join(','),
        },
      }),
    }),
  }),
})
